import { Alert, Pagination, Radio, Row, Select, Space } from "antd";
import useNotification from '@/hooks/notifications';
import { useEffect, useMemo, useState } from "react";
import useFetchListData from "../../hooks/useFetchListData";
import HighlightedTable from '@Components/Tables/HighlightedTable';
import Search from "antd/es/input/Search";
import { highlightItemPost } from "../../utils/fetchFunctions/highlight";
import SortableTable from '@Components//Tables/SortableTable';
import { useSelector } from "react-redux";
import authSelector from '@Redux/auth/authSelector';
import { postData } from "@/utils/api";

const searchTypes = [
	{ label: 'Search by reference ID', value: 'topicKey' },
	{ label: 'Search by keywords', value: 'search' },
];

const types = [
	{ label: 'VIDEOS', value: 'EPV_VIDEO_FOOTAGE%3BEPV_EDITED_VIDEOS' },
	{ label: 'PHOTOSETS', value: 'EPV_PHOTOSET' },
	{ label: 'PHOTOS', value: 'EPV_PHOTO' },
];

export default function SpotlightedTopicMedias({topicKey}) {
  const {openNotification} = useNotification();
	const { jwttoken } = useSelector(authSelector);
  const [loadingChange, setLoadingChange] = useState(false);
	const [type, setType] = useState('EPV_VIDEO_FOOTAGE%3BEPV_EDITED_VIDEOS');

  const params = {url: `topics/${topicKey}/medias/${type}`};
	const { data, loading, searchParams, setParam, setParams, getData, pagination, error, deleteParam, setDefaultParams } = useFetchListData({params, newFunctions: true});

	const selectedMediaParams = {url: `topics/${topicKey}/spotlighted/medias`};
	const selectedMedia = useFetchListData({params: selectedMediaParams, newFunctions: true});

	const sortedData = useMemo(() => selectedMedia.data ? [...selectedMedia.data]?.sort((a, b) => b?.position - a?.position) : [], [selectedMedia.data]);

  useEffect(() => {
		if (error) {
			openNotification({message: 'Something went wrong!', description: error.message || null, type: 'error'});
		}
	}, [error]);

  function handlePagination(page, pageSize) {
		setParams({page, pageSize});
	}

  const handleSearch = () => {
		setParams({sort: 'relevancy:asc'});
	};

	const handleType = ({ target: { value } }) => {
		deleteParam('page', false);
		setType(value);
		setDefaultParams({url: `topics/${topicKey}/medias/${value}`});
	};

	async function select (id) {
		setLoadingChange(true);

		const res = await highlightItemPost({url: `topics/${topicKey}/spotlight/media`, id, name: null});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingChange(false);
			return;
		}
    await Promise.all([getData(), selectedMedia.getData()]);
		setLoadingChange(false);
	}

	async function unselect (id) {
		setLoadingChange(true);
		const res = await highlightItemPost({url: `topics/${topicKey}/spotlight/media`, id, name: null, highlighted: false});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingChange(false);
			return;
		}

		await Promise.all([getData(), selectedMedia.getData()]);
		setLoadingChange(false);
	}

	const saveItems = async (items) => {
		setLoadingChange(true);
		const mediaIds = items
			.sort((a, b) => a.position - b.position)
			.reduce((acc, {id}) => acc + id + ';', '');

		const {error, message} = await postData({endpoint: `/topics/${topicKey}/featured-order?mediaIds=${mediaIds}`, noResponse: true, auth: jwttoken});

		if (error) {
			openNotification({message: 'Something went wrong!', description: message || null, type: 'error'});
		}

		await selectedMedia.getData();

		setLoadingChange(false);
	};

	return (
    <Space size="large" direction='vertical' style={{width: '100%'}} >
      <h2>Selected Media</h2>
			<Alert
					description={
							<>To display media cards below the spotlighted topic, <b>it is necessary to select 0, 3 or 4 items.</b> If you select 1 or 2 items, the spotlighted topic will not be displayed in the homepage.</>}
					type="info"
					style={{marginBottom: 20}}
			/>
			<SortableTable
				data= {sortedData}
				loading={selectedMedia.loading || loadingChange}
				saveItems={saveItems}
				idKey='mediaId'
				modifiedDate
				select={select}
				unselect={unselect}
				selectedKey="spotlighted"
				mediaTypePreview
			/>
      <h2>Media associated with the selected topic</h2>
			<Radio.Group
					options={types}
					onChange={handleType}
					value={type}
					optionType="button"
					size="large"
			/>
      <Search
				addonBefore={
					<Select
						style={{width: 190}}
						options={searchTypes}
						value={searchParams.get("searchType") || 'search'}
						onChange={setParam('searchType')}
					/>
				}
				value={searchParams.get("search") || ''}
				onChange={setParam('search', false)}
				onSearch={handleSearch}
				enterButton
			/>
      <HighlightedTable
				data={ data }
				loading={loading || loadingChange}
				sort={searchParams.get("sort") || 'mediaDate:desc'}
				setSort={setParam('sort')}
				getListData={getData}
				type="topic"
				select={select}
				unselect={unselect}
        selectedKey="spotlighted"
				modifiedDate
				mediaTypePreview
			/>
      <Row justify="center">
					<Pagination
						total={pagination.totalElements}
						pageSize={pagination.pageSize}
						current={pagination.page}
						showSizeChanger
						showQuickJumper
						hideOnSinglePage
						disabled={loading}
						onChange={handlePagination}
					/>
				</Row>
    </Space>

  );
}